/**
 * Fix pour l'erreur ResizeObserver provenant de react-popper et @popperjs/core
 * 
 * Cette erreur se produit lorsque le ResizeObserver ne peut pas livrer toutes les notifications
 * dans un seul frame d'animation, ce qui est généralement inoffensif mais pollue la console.
 * 
 * Sources du problème :
 * - react-popper (utilisé dans DatePicker/BasePicker)
 * - @popperjs/core (utilisé dans Dropdown)
 * - react-custom-scrollbars-2 (utilisé dans SideNav)
 */

export const suppressResizeObserverErrors = () => {
  // Pattern pour détecter les erreurs ResizeObserver
  const resizeObserverErr = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;

  // Intercepte console.error
  const originalError = console.error;
  console.error = (...args) => {
    const firstArg = args[0];
    if (typeof firstArg === 'string' && resizeObserverErr.test(firstArg)) {
      return; // Ignore cette erreur non critique
    }
    originalError.apply(console, args);
  };

  // Intercepte les erreurs window non gérées
  window.addEventListener('error', (event) => {
    if (resizeObserverErr.test(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  });
};

/**
 * Wrapper pour ResizeObserver avec debounce pour éviter les boucles
 */
export class DebouncedResizeObserver {
  constructor(callback, delay = 100) {
    this.callback = callback;
    this.delay = delay;
    this.timeout = null;
    
    this.observer = new ResizeObserver((entries, observer) => {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      
      this.timeout = setTimeout(() => {
        this.callback(entries, observer);
      }, this.delay);
    });
  }

  observe(target, options) {
    this.observer.observe(target, options);
  }

  unobserve(target) {
    this.observer.unobserve(target);
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.observer.disconnect();
  }
}
